import { useMemo } from 'react';

import useStatementVerificationEnabled from 'modules/Onboarding/AccountingSetup/StatementUpload/hooks/useStatementVerificationEnabled';
import { accountKey, missingPeriods } from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import { useAccountingOnboardingProgress } from 'services/bookkeeping/queries/useAccountingOnboardingProgress';
import useStatementCoverage, {
    AccountStatementCoverage,
    isCoverageUsable,
} from 'services/bookkeeping/queries/useStatementCoverage';

interface StatementReadiness {
    /** Whether the verified, coverage-driven statement path is on for this member. */
    isEnabled: boolean;
    /** Every account either fully covered or explicitly waived. False while coverage loads. */
    isReady: boolean;
    /**
     * Every account satisfied or waived *against coverage we could actually read*. Implies
     * `isEnabled`, so a caller needs no second flag check.
     *
     * Deliberately stricter than `isReady`, which answers the opposite question. `isReady` gates
     * Continue, so everything the member cannot act on has to read as ready or the step traps them
     * — an unreadable coverage payload and an unresolved books start date included. This gates
     * *completing* the step, which dispatches the transaction-extraction fan-out and cannot be
     * taken back, so those two cases must read as unverified: the section renders a message and no
     * upload cards in either, so nothing was uploaded and there is nothing to fan out over.
     */
    isVerified: boolean;
    /**
     * Accounts the member was asked for that still have months missing — waived or not.
     *
     * Deliberately not the inverse of `isReady`. A waiver makes an account ready to move past;
     * it does not make the statements arrive, and the prompt shown on Complete is about what is
     * still missing rather than what is blocking. Empty whenever coverage is unusable or still
     * loading, so a caller cannot mistake "we could not look" for "nothing outstanding".
     */
    outstandingAccounts: AccountStatementCoverage[];
    /**
     * Whether `VerifiedStatementUploadSection` will render upload cards, as opposed to a skeleton,
     * one of its two explanatory messages, or nothing at all.
     *
     * Lives here rather than in the step so there is one copy of the predicate. The section's four
     * early returns are the contract: it renders a skeleton while coverage is pending, a warning
     * when the payload is unreadable, an info message when the books start date has not resolved,
     * and `null` when there are no accounts — cards only past all four. A caller that re-derives
     * this from `useStatementCoverage` stops mirroring the section the moment a return is added.
     *
     * The step needs it to decide whether to ask "why do you need my bank statements?" above the
     * section: a question about statements sitting over a checklist with none on it is worse than
     * no question. Free to ask — React Query shares the fetch with the section.
     */
    hasUploadCards: boolean;
}

/**
 * The statement half of the Accounting Setup document step's Continue gate.
 *
 * Derived from the queries rather than reported upward from the section, so the step and the
 * section can ask independently; React Query shares the fetch, so asking twice is free.
 *
 * Four cases count as ready rather than blocking, all because the member cannot act on them:
 * no required periods (books start date unresolved, so nothing is being asked and nothing
 * could be waived), coverage unavailable, institution unresolved (the rectifier drops
 * empty-named accounts, so the months would never clear), and a failed first load — caught
 * explicitly, because falling through would pass vacuously over an empty account list.
 *
 * A failed *refetch* is none of those: React Query keeps the last good `data`, so the verdict
 * is still derivable and must be derived. `isCoverageUsable` draws that line for both callers.
 */
interface StatementReadinessOptions {
    /**
     * False for a step that renders no statements section: coverage is not fetched, and every
     * field reads as nothing being asked — ready, unverified, nothing outstanding, no cards.
     * `isEnabled` still reports the gate, so the caller's other gate-dependent behaviour holds.
     */
    enabled?: boolean;
    /**
     * True for a step whose only upload is the verified section, so it verifies for every member the
     * gate lets in, whatever its audience filter says. The gate itself still applies: a host using
     * this must not register the step while `useStatementVerificationEnabled` reads off.
     */
    ignoreAudience?: boolean;
}

export default function useStatementReadiness({
    enabled = true,
    ignoreAudience = false,
}: StatementReadinessOptions = {}): StatementReadiness {
    const isGateOn = useStatementVerificationEnabled({ ignoreAudience });
    // Everything below reads this rather than the gate, which is only reported back.
    const isEnabled = isGateOn && enabled;
    // `isPending`, not `isLoading`. The query is conditionally enabled (it waits on
    // `memberId`), and `isLoading` is `isPending && isFetching` — so while it is disabled
    // the flag reads false with `data` still undefined, dropping straight past this guard
    // into "not usable" and unlocking Continue with nothing verified. `isPending` covers
    // disabled and first-fetch alike.
    const { data: coverage, isPending: isCoveragePending } = useStatementCoverage({ enabled: isEnabled });
    const { data: progress } = useAccountingOnboardingProgress();

    const acknowledgedAccounts = useMemo(
        () => new Set(progress?.statement_upload_data?.acknowledged_accounts ?? []),
        [progress?.statement_upload_data?.acknowledged_accounts]
    );

    const isReady = useMemo(() => {
        if (!isEnabled) {
            // The legacy path gates on its own per-account rows; nothing to add here.
            return true;
        }
        if (isCoveragePending) {
            return false;
        }
        if (!isCoverageUsable(coverage)) {
            return true;
        }
        return coverage.accounts.every(
            (account) =>
                account.required_periods.length === 0 ||
                // Strict, so an older payload without the field reads as "resolved" rather
                // than waving an account through on `undefined`.
                account.institution_unresolved === true ||
                missingPeriods(account).length === 0 ||
                acknowledgedAccounts.has(accountKey(account))
        );
    }, [isEnabled, isCoveragePending, coverage, acknowledgedAccounts]);

    /** The two vacuous passes `isReady` grants on purpose, withheld here — see `isVerified` above. */
    const isVerified = useMemo(() => {
        if (!isEnabled || isCoveragePending) {
            return false;
        }
        if (!isCoverageUsable(coverage) || coverage.coverage_start == null) {
            return false;
        }
        // Past those guards `isReady` is the per-account verdict and nothing else.
        return isReady;
    }, [isEnabled, isCoveragePending, coverage, isReady]);

    const outstandingAccounts = useMemo(() => {
        if (!isEnabled || isCoveragePending || !isCoverageUsable(coverage)) {
            return [];
        }
        return coverage.accounts.filter(
            (account) =>
                account.required_periods.length > 0 &&
                account.institution_unresolved !== true &&
                missingPeriods(account).length > 0
        );
    }, [isEnabled, isCoveragePending, coverage]);

    /** One per early return in VerifiedStatementUploadSection, in the same order — see the field. */
    const hasUploadCards = useMemo(() => {
        if (!isEnabled || isCoveragePending) {
            return false;
        }
        if (!isCoverageUsable(coverage) || coverage.coverage_start == null) {
            return false;
        }
        return coverage.accounts.length > 0;
    }, [isEnabled, isCoveragePending, coverage]);

    return { isEnabled: isGateOn, isReady, isVerified, outstandingAccounts, hasUploadCards };
}
